import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Plus, Camera, Mic } from 'lucide-react'
import { api } from '../../lib/api.js'
import { toast } from '../../components/feedback/Toast.jsx'
import { fmtDay, useCanEdit, canTouch, primeKeyboard, deliveryHref } from './lib.js'
import { Chip } from './ProjectsPage.jsx'
import BackBar from './BackBar.jsx'
import SwipeRow from '../../components/SwipeRow.jsx'
import Initials from './Initials.jsx'
import { ProjectFiles } from './SessionFiles.jsx'

/**
 * Project detail — the recall moment lives here. A new session is almost
 * always yesterday's setup plus edits, so "start from the latest session"
 * is the primary action and a blank session is the quiet alternative.
 */
/**
 * The days' shared setup, under the project name (Charles, 2026-08-03):
 * mics and sample rate, shown when every day that recorded them agrees —
 * a blank day inherits day 1 rather than conflicting. Disagreement shows
 * red "conflicting entries" instead of guessing a winner; open the days
 * to see which is right. Red flags a problem here, nothing is armed.
 */
function SetupSummary({ setup }) {
  if (!setup) return null
  const parts = []
  if (setup.micsConflict) {
    parts.push(<span key="m" className="font-medium text-danger">mics: conflicting entries</span>)
  } else if (setup.mics?.length) {
    parts.push(<span key="m" className="inline-flex items-baseline gap-1"><Mic size={12} className="self-center" />{setup.mics.join(', ')}</span>)
  }
  if (setup.rateConflict) {
    parts.push(<span key="r" className="font-medium text-danger">sample rate: conflicting entries</span>)
  } else if (setup.sampleRate) {
    parts.push(<span key="r">{setup.sampleRate}</span>)
  }
  if (setup.editConflict) {
    parts.push(<span key="e" className="font-medium text-danger">edit goes to: conflicting entries</span>)
  } else if (setup.editGoesTo) {
    parts.push(
      deliveryHref(setup.editGoesTo) ? (
        <a key="e" href={deliveryHref(setup.editGoesTo)} className="text-link hover:underline">
          edit goes to {setup.editGoesTo}
        </a>
      ) : (
        <span key="e">edit goes to {setup.editGoesTo}</span>
      )
    )
  }
  if (parts.length === 0) return null
  return (
    <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-sm text-muted">
      {parts.map((p, i) => (
        <span key={i} className="inline-flex items-baseline gap-x-2">
          {i > 0 && <span className="text-faint">·</span>}
          {p}
        </span>
      ))}
    </div>
  )
}

/**
 * The multi-day session's name, tap-to-rename for anyone signed in
 * (Charles, 2026-08-03: there was no way to rename one). Same in-place
 * pattern as the rest of the log — input on tap, saves on blur/Enter.
 */
function ProjectName({ project, canEdit }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(project.name)

  async function save() {
    setEditing(false)
    const name = val.trim()
    if (!name || name === project.name) return setVal(project.name)
    try {
      await api(`/log/projects/${project.id}`, { method: 'PATCH', body: { name } })
      qc.invalidateQueries({ queryKey: ['sl-project', String(project.id)] })
      qc.invalidateQueries({ queryKey: ['sl-projects'] })
    } catch (err) {
      toast(err.message || 'Couldn’t rename', 'error')
      setVal(project.name)
    }
  }

  if (editing) {
    return (
      <input
        autoFocus
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur()
          if (e.key === 'Escape') {
            setVal(project.name)
            setEditing(false)
          }
        }}
        className="w-full min-w-0 border-0 bg-transparent p-0 font-serif text-2xl font-semibold text-ink focus:outline-none"
        aria-label="Session name"
      />
    )
  }

  return (
    <h1
      onClick={() => canEdit && setEditing(true)}
      title={canEdit ? 'Tap to rename' : undefined}
      className="min-w-0 truncate text-2xl font-semibold text-ink"
    >
      {project.name}
    </h1>
  )
}

export default function ProjectPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const { data, isLoading } = useQuery({
    queryKey: ['sl-project', projectId],
    queryFn: () => api(`/log/projects/${projectId}`),
  })
  const canEdit = useCanEdit(me)

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>
  if (!data?.project) return <p className="text-sm text-muted">No such project.</p>

  const { project, sessions, setup } = data
  // Sessions list oldest → newest, so "the latest" is the last row.
  const latest = sessions[sessions.length - 1]
  const wrapped = project.status === 'wrapped'
  // Guests create days; renaming/wrapping/deleting is for the project's
  // creator (or any editor+).
  const canModifyProject = canTouch(me, project)

  async function deleteDay(s) {
    if (!window.confirm(`Delete “${s.label || 'Session'}”?`)) return
    try {
      await api(`/log/sessions/${s.id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['sl-project', projectId] })
      qc.invalidateQueries({ queryKey: ['sl-projects'] })
    } catch (err) {
      toast(err.message || 'Couldn’t delete', 'error')
    }
  }

  async function toggleWrapped() {
    try {
      await api(`/log/projects/${project.id}`, {
        method: 'PATCH',
        body: { status: wrapped ? 'active' : 'wrapped' },
      })
      qc.invalidateQueries({ queryKey: ['sl-project', projectId] })
      qc.invalidateQueries({ queryKey: ['sl-projects'] })
      if (!wrapped) navigate('/log') // wrapping = done here, back to the shelf
    } catch (err) {
      toast(err.message || 'Couldn’t update project', 'error')
    }
  }

  return (
    <div>
      <BackBar to="/log" label="Session Log" />

      <div className="mb-4 border-b border-hairline pb-3">
        <div className="flex items-baseline gap-2">
          <ProjectName project={project} canEdit={canModifyProject} />
          <Chip kind={project.kind} />
          {wrapped && <span className="section-header">wrapped</span>}
        </div>
        {(project.client || project.location) && (
          <div className="mt-1 text-sm text-muted">
            {[project.client, project.location].filter(Boolean).join(' · ')}
          </div>
        )}
        <SetupSummary setup={setup} />
      </div>

      {canEdit && !wrapped && (
        <div className="mb-4 space-y-2">
          <button
            onClick={() => { primeKeyboard(); navigate(latest ? `/log/${project.id}/new?from=${latest.id}` : `/log/${project.id}/new`) }}
            className="tap flex w-full items-center justify-center gap-2 rounded-xl bg-navy px-4 py-3 text-sm font-medium text-white"
          >
            <Plus size={16} />
            {latest ? 'New session — using data from previous entry' : 'First session'}
          </button>
          {latest && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => { primeKeyboard(); navigate(`/log/${project.id}/new`) }}
                className="tap text-xs text-muted underline underline-offset-2"
              >
                or start blank
              </button>
            </div>
          )}
        </div>
      )}

      <section>
        <h2 className="section-header mb-2">
          {sessions.length} day{sessions.length === 1 ? '' : 's'}
        </h2>
        {sessions.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted">Nothing logged yet.</p>
        ) : (
          <div className="card divide-y divide-hairline">
            {sessions.map((s) => (
              <SwipeRow key={s.id} enabled={canTouch(me, s)} onDelete={() => deleteDay(s)}>
                <Link
                  to={`/log/${project.id}/s/${s.id}`}
                  className="flex items-center gap-3 px-4 py-3 active:bg-grouped"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink">
                      {s.label || 'Session'}
                      {s.recalledFrom && <span className="ml-2 text-xs font-normal text-faint">recalled</span>}
                    </div>
                    <div className="mt-0.5 text-xs text-muted">{fmtDay(s.date)}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2.5 text-xs text-faint">
                    {s.gearCount > 0 && (
                      <span className="flex items-center gap-1"><Mic size={13} />{s.gearCount}</span>
                    )}
                    {s.photoCount > 0 && (
                      <span className="flex items-center gap-1"><Camera size={13} />{s.photoCount}</span>
                    )}
                    <Initials name={s.by} />
                  </div>
                  <ChevronRight size={16} className="shrink-0 text-faint" />
                </Link>
              </SwipeRow>
            ))}
          </div>
        )}
      </section>

      {/* The project's master folder on Current Projects (each day's page
          shows its own folder); after the days, so the list and the New
          session button stay where the thumb expects them. */}
      <div className="mt-6">
        <ProjectFiles projectId={project.id} dayCount={sessions.length} />
      </div>

      {canModifyProject && (
        <div className="mt-8 text-center">
          <button onClick={toggleWrapped} className="tap text-xs text-muted underline underline-offset-2">
            {wrapped ? 'Reopen' : 'Wrap — no more days coming'}
          </button>
        </div>
      )}
    </div>
  )
}
